import type { WhiteLabelData } from "@toruslabs/openlogin-utils";
import { LOGIN_PROVIDER, OPENLOGIN_NETWORK, OPENLOGIN_NETWORK_TYPE, storageAvailable } from "@toruslabs/openlogin-utils";
import log, { LogLevelDesc } from "loglevel";

import { LOCAL_STORAGE_KEY, LOGIN_CONFIG, SESSION_STORAGE_KEY } from "./utils/enums";

const { VUE_APP_TORUS_NETWORK, NODE_ENV } = process.env;

const baseUrl = window.location.origin;

const baseRoute = baseUrl + process.env.BASE_URL;

const redirectURI = `${baseUrl}/redirect`;

const { hash, search } = window.location;
const hashParams = new URLSearchParams(hash.slice(1));
const dappStorageKey = hashParams.get("dappStorageKey") || "";

let logLevel: LogLevelDesc;
let commonApiHost: string;
let api: string;
let topperApiHost: string;
let proxy: string;

switch (NODE_ENV) {
  case "development":
    logLevel = "debug";
    commonApiHost = "http://localhost:2022/common";
    api = "http://localhost:2022";
    topperApiHost = "http://localhost:2022";
    proxy = "http://localhost:2022/proxies";

    // commonApiHost = "https://xrpl-lrc.tor.us/common";
    // api = "https://xrpl-lrc.tor.us";
    // topperApiHost = "https://xrpl-lrc.tor.us";
    // proxy = "https://xrpl-lrc.tor.us/proxies";
    break;
  case "staging":
    logLevel = "debug";
    commonApiHost = "https://xrpl-lrc.tor.us/common";
    api = "https://xrpl-lrc.tor.us";
    topperApiHost = "https://xrpl-lrc.tor.us";
    proxy = "https://xrpl-lrc.tor.us/proxies";
    break;
  case "production":
  default:
    logLevel = "error";
    commonApiHost = "https://xrpl-prod.tor.us/common";
    api = "https://xrpl-prod.tor.us";
    topperApiHost = "https://xrpl-prod.tor.us";
    proxy = "https://xrpl-prod.tor.us/proxies";
    break;
}
log.setLevel(logLevel);

// search also has a ? in the value.
const finalUrl = new URL(`${baseUrl}?${hash.slice(1)}&${search.slice(1)}`);

const isCustomLogin = finalUrl.searchParams.get("isCustomLogin");
const namespace = finalUrl.searchParams.get("namespace");
const sessionId = finalUrl.searchParams.get("sessionId");

// const state = finalUrl.searchParams.get("state");

// by default value should be false as user can open
// torus wallet directly also and they dont start journey
// from /start or /end url which is the case for custom dapps login.
const isCustomDapp = window.self !== window.top;

// // state is presend in the /start url.
// if (state) {
//   const decodedState = JSON.parse(safeatob(decodeURIComponent(decodeURIComponent(state))));
//   // this means that it's from another domain
//   isCustomDapp = decodedState?.origin?.hostname !== window.location.hostname;
// }

export default {
  baseUrl,
  baseRoute,
  commonApiHost,
  metadataHost: "https://metadata.tor.us",
  api,
  proxy,
  redirect_uri: redirectURI,
  dappStorageKey,
  // we do the isCustomDapp check to differentiate b/w app.tor.us and older dapps without isCustomLogin flag
  isCustomLogin: () => {
    if (isCustomLogin === "true") {
      return true;
    }
    if (isCustomLogin === "false") return false;
    if (isCustomDapp === null) return null;
    return false;
  },
  namespace,
  sessionId,
  storageServerUrl: "https://broadcast-server.tor.us",
  storageAvailability: {
    local: storageAvailable("localStorage"),
    session: storageAvailable("sessionStorage"),
  } as Record<string, boolean>,
  supportedCurrencies: ["USD", "AUD", "CAD", "EUR", "GBP", "HKD", "IDR", "INR", "JPY", "PHP", "RUB", "SGD", "UAH"],
  logosUrl: "https://images.toruswallet.io",

  isStorageAvailable: {
    [LOCAL_STORAGE_KEY]: storageAvailable(LOCAL_STORAGE_KEY),
    [SESSION_STORAGE_KEY]: storageAvailable(SESSION_STORAGE_KEY),
  },

  torusNetwork: VUE_APP_TORUS_NETWORK as OPENLOGIN_NETWORK_TYPE,
  openLoginClientId: {
    [OPENLOGIN_NETWORK.SAPPHIRE_MAINNET]: "BGBOIc_oDLDvaSQNweb7130PTBkO7CHQBJVTXFY7P_t9EG4ozdfgs5hhv4B1mTYqcw4MX-sUNs_nsoIdAOTLlgA",
    [OPENLOGIN_NETWORK.CYAN]: "BD4odzHxwjINGbgOE6eJnQIGzE2HLkxV_4leLl8YkgKFDR9t40qBj-AWT1cjvy0s9SK1deZ50J2IJ5KRsdxoR64",
    [OPENLOGIN_NETWORK.TESTNET]:
      process.env.VUE_APP_TORUS_BUILD_ENV === "development"
        ? "BNq_7NFgNR5m9CCSgjE_OkgvHE4dctzepK1ln0-gW4b4a20cIbBok9kxut9euYxxOszENaisR3vquP_pACTndEY"
        : "BGijtUtPP5GuXsSw9flk97XUa_kbZLCKMa_V8A4fc6AcPhcrIUg1GRRH8ZcWo5x86OxtSS3ST6C-_ChhdbmSw4w",
    [OPENLOGIN_NETWORK.SAPPHIRE_DEVNET]: "BLTpoP_wt2h8oVIWHenLqzm8FLTvpUoDYlc3Sb2f-8ivsBBuvpakjVXA0EQYXcbq_qtaaukF4oLT3XSGkW6rLzc",
  } as Record<OPENLOGIN_NETWORK_TYPE, string>,
  openLoginWhiteLabel: {
    dark: false,
    theme: {
      primary: "#000000",
    },
    // TODO: fill whitelabel info
    // name: "Casper Wallet",
    // logo: "https://images.toruswallet.io/casper.svg",
  } as WhiteLabelData,

  simplexApiHost: "https://simplex-api.tor.us",
  topperApiHost,

  // key is the login provider
  loginConfig: {
    [LOGIN_PROVIDER.GOOGLE]: {
      loginProvider: LOGIN_PROVIDER.GOOGLE,
      name: "Google",
      description: "login.verifierGoogleDesc",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      showOnModal: true,
      mainOption: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.FACEBOOK]: {
      loginProvider: LOGIN_PROVIDER.FACEBOOK,
      description: "",
      name: "Facebook",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      showOnModal: true,
      mainOption: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.TWITTER]: {
      loginProvider: LOGIN_PROVIDER.TWITTER,
      description: "",
      name: "Twitter",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      showOnModal: true,
      mainOption: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.DISCORD]: {
      loginProvider: LOGIN_PROVIDER.DISCORD,
      description: "",
      name: "Discord",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      showOnModal: true,
      mainOption: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.LINE]: {
      loginProvider: LOGIN_PROVIDER.LINE,
      description: "",
      name: "LINE",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.REDDIT]: {
      loginProvider: LOGIN_PROVIDER.REDDIT,
      description: "",
      name: "Reddit",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: false,
      showOnDesktop: false,
      showOnMobile: false,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.APPLE]: {
      loginProvider: LOGIN_PROVIDER.APPLE,
      description: "",
      name: "Apple",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: true,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.GITHUB]: {
      loginProvider: LOGIN_PROVIDER.GITHUB,
      description: "",
      name: "GitHub",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: true,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.TWITCH]: {
      loginProvider: LOGIN_PROVIDER.TWITCH,
      description: "",
      name: "Twitch",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.LINKEDIN]: {
      loginProvider: LOGIN_PROVIDER.LINKEDIN,
      description: "",
      name: "LinkedIn",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.WECHAT]: {
      loginProvider: LOGIN_PROVIDER.WECHAT,
      description: "",
      name: "WeChat",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: false,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.KAKAO]: {
      loginProvider: LOGIN_PROVIDER.KAKAO,
      description: "",
      name: "Kakao",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: false,
      buttonDescription: "",
    },
    [LOGIN_PROVIDER.EMAIL_PASSWORDLESS]: {
      loginProvider: LOGIN_PROVIDER.EMAIL_PASSWORDLESS,
      description: "login.verifierEmailDesc",
      name: "email",
      logoHover: "",
      logoLight: "",
      logoDark: "",
      mainOption: false,
      showOnModal: true,
      showOnDesktop: true,
      showOnMobile: true,
      // For torus only
      hasLightLogo: true,
      buttonDescription: "",
    },
  } as Record<string, LOGIN_CONFIG>,
};
